// Single source of truth for the image hosts the self-hosted optimizer may
// fetch. `next.config.ts` derives its `images.remotePatterns` from this list,
// so a host added here is optimizable everywhere at once.
export const OPTIMIZABLE_REMOTE_HOSTS = [
  "images.unsplash.com",
  "image.tmdb.org",
  "flagcdn.com",
  "flags.restcountries.com",
  "upload.wikimedia.org",
  "cdn-v3.huislijn.nl",
  "cdn-v4.huislijn.nl",
  "ap.rdcpix.com",
  "shared.akamai.steamstatic.com",
  "cdn.cloudflare.steamstatic.com",
  "images.pokemontcg.io",
  "yt3.googleusercontent.com",
  "yt3.ggpht.com",
  "is1-ssl.mzstatic.com",
  "www.apple.com",
  "a.espncdn.com",
  "archives.bulbagarden.net",
  "caimages.collectors.com",
  "d1htnxwo4o0jhw.cloudfront.net",
  "firebasestorage.googleapis.com",
  "raw.githubusercontent.com",
  "coverartarchive.org",
  "cdn.myanimelist.net",
  "covers.openlibrary.org",
  "cdn-images.dzcdn.net",
  "img.a.transfermarkt.technology",
  "hips.hearstapps.com",
  "vz.cnwimg.com",
] as const

const OPTIMIZABLE_REMOTE_HOST_SET: ReadonlySet<string> = new Set(
  OPTIMIZABLE_REMOTE_HOSTS,
)

const OPTIMIZABLE_EXTENSIONS = new Set([".jpg", ".jpeg", ".png", ".webp", ".avif"])
const SKIPPED_EXTENSIONS = new Set([".svg", ".gif", ".pdf", ".tif", ".tiff"])
const WIKIMEDIA_THUMBNAIL_WIDTHS = [
  20, 40, 60, 120, 250, 330, 500, 960, 1280, 1920, 3840,
]

const DEFAULT_IMAGE_WIDTH = 1200
const DEFAULT_IMAGE_QUALITY = 75
// Game photography is darkened by the UI and never needs editorial-level
// resolution. These widths are both accepted by the Next.js optimizer and
// materially reduce bytes compared with the previous 1200px/75 defaults.
export const GAME_PANEL_IMAGE_WIDTH = 1080
export const GAME_PANEL_IMAGE_QUALITY = 60
export const GAME_THUMBNAIL_IMAGE_WIDTH = 640
export const GAME_THUMBNAIL_IMAGE_QUALITY = 60
// Bump only after a fully reviewed built-in image manifest is accepted. The
// token is added to the optimizer's upstream URL so Next.js cannot reuse an
// older one-year provider response for the same mutable source URL.
export const REVIEWED_IMAGE_CACHE_REVISION = "reviewed-v14-2026-07-24"

export function normalizeImageUrl(src: string | null | undefined) {
  const trimmedSource = src?.trim()
  if (!trimmedSource) return null

  // URLs copied from HTML occasionally retain the entity-encoded query
  // separators. Browsers treat those as literal text in an img src.
  const decodedSource = trimmedSource.replace(/&amp;/gi, "&")

  if (decodedSource.startsWith("//")) {
    return `https:${decodedSource}`
  }

  if (decodedSource.startsWith("/")) {
    const rawPath = decodedSource.split(/[?#]/, 1)[0]!
    let decodedPath: string
    try {
      decodedPath = decodeURIComponent(rawPath)
    } catch {
      return null
    }
    if (
      rawPath.includes("\\") ||
      /[\u0000-\u001f\u007f]/.test(rawPath) ||
      /%2e|%2f|%5c/i.test(rawPath) ||
      decodedPath
        .split("/")
        .some((segment) => segment === "." || segment === "..")
    ) {
      return null
    }
    return decodedSource
  }

  try {
    const parsed = new URL(decodedSource)
    if (parsed.protocol === "http:") {
      // HTTP images are blocked as mixed content on the production site.
      parsed.protocol = "https:"
      return parsed.toString()
    }

    if (parsed.protocol !== "https:") {
      return null
    }
  } catch {
    return null
  }

  return decodedSource
}

function getPathExtension(pathname: string) {
  const match = pathname.toLowerCase().match(/\.[a-z0-9]+$/)
  return match?.[0] ?? null
}

export function canOptimizeImageUrl(src: string | null | undefined) {
  if (!src) return false
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return false
  if (src.startsWith("/_next/image?")) return false
  if (src.startsWith("/api/custom-games/")) return false

  try {
    if (src.startsWith("/")) {
      const extension = getPathExtension(src.split("?")[0])
      return !extension || OPTIMIZABLE_EXTENSIONS.has(extension)
    }

    const parsed = new URL(src)
    if (parsed.protocol !== "https:") return false
    if (!OPTIMIZABLE_REMOTE_HOST_SET.has(parsed.hostname.toLowerCase())) return false

    const extension = getPathExtension(parsed.pathname)
    if (extension && SKIPPED_EXTENSIONS.has(extension)) return false
    return !extension || OPTIMIZABLE_EXTENSIONS.has(extension)
  } catch {
    return false
  }
}

function withReviewedImageRevision(src: string) {
  try {
    const local = src.startsWith("/")
    const parsed = new URL(src, local ? "https://playhigherlower.invalid" : undefined)
    parsed.searchParams.set("_hl_image", REVIEWED_IMAGE_CACHE_REVISION)
    return local ? `${parsed.pathname}${parsed.search}${parsed.hash}` : parsed.toString()
  } catch {
    return src
  }
}

function withProviderImageWidth(src: string, width: number) {
  try {
    const parsed = new URL(src)
    const hostname = parsed.hostname.toLowerCase()
    if (
      hostname === "image.tmdb.org" &&
      parsed.pathname.includes("/t/p/original/")
    ) {
      // TMDB originals are commonly close to 1 MB. Its official derivative
      // is much faster to retrieve and still comfortably exceeds the final
      // game-panel output size.
      const providerWidth = width <= 780 ? "w780" : "w1280"
      parsed.pathname = parsed.pathname.replace(
        "/t/p/original/",
        `/t/p/${providerWidth}/`
      )
      return parsed.toString()
    }
    if (hostname === "upload.wikimedia.org") {
      // Wikimedia rejects arbitrary hotlinked thumbnail widths. Round up to
      // its published production buckets so the CDN can serve a cached
      // derivative instead of returning 400/429 responses.
      const requestedWidth = Math.max(Math.round(width), 20)
      const providerWidth =
        WIKIMEDIA_THUMBNAIL_WIDTHS.find(
          (candidate) => candidate >= requestedWidth
        ) ?? WIKIMEDIA_THUMBNAIL_WIDTHS.at(-1)!
      const segments = parsed.pathname.split("/")
      const thumbIndex = segments.indexOf("thumb")
      if (thumbIndex >= 0 && segments.length > thumbIndex + 4) {
        const originalFile = segments[segments.length - 2]
        if (originalFile) {
          segments[segments.length - 1] = `${providerWidth}px-${originalFile}`
          parsed.pathname = segments.join("/")
          return parsed.toString()
        }
      }
      if (
        thumbIndex < 0 &&
        parsed.pathname.startsWith("/wikipedia/commons/") &&
        /\.(?:jpe?g|png|webp)$/i.test(parsed.pathname)
      ) {
        const originalFile = segments.at(-1)
        if (originalFile) {
          segments.splice(3, 0, "thumb")
          segments.push(`${providerWidth}px-${originalFile}`)
          parsed.pathname = segments.join("/")
          return parsed.toString()
        }
      }
    }
  } catch {
    // Local paths and malformed sources are handled by the normal fallback.
  }
  return src
}

export function getProviderSizedImageUrl(
  src: string | null | undefined,
  width: number
) {
  const normalizedSource = normalizeImageUrl(src)
  return normalizedSource
    ? withProviderImageWidth(normalizedSource, width)
    : null
}

export function shouldLoadProviderImageDirectly(
  src: string | null | undefined
) {
  const normalizedSource = normalizeImageUrl(src)
  if (!normalizedSource || normalizedSource.startsWith("/")) return false

  try {
    // Wikimedia throttles requests from the shared optimizer server.
    return new URL(normalizedSource).hostname.toLowerCase() === "upload.wikimedia.org"
  } catch {
    return false
  }
}

export function getReviewedImageUrl(src: string | null | undefined) {
  const normalizedSource = normalizeImageUrl(src)
  if (!normalizedSource) return null
  return canOptimizeImageUrl(normalizedSource)
    ? withReviewedImageRevision(normalizedSource)
    : normalizedSource
}

export function getOptimizedImageUrl(
  src: string | null | undefined,
  options: { width?: number; quality?: number } = {}
) {
  const normalizedSource = normalizeImageUrl(src)
  if (!normalizedSource) return null
  if (!canOptimizeImageUrl(normalizedSource)) return normalizedSource

  const width = options.width ?? DEFAULT_IMAGE_WIDTH
  const quality = options.quality ?? DEFAULT_IMAGE_QUALITY
  const providerSizedSource = withProviderImageWidth(normalizedSource, width)
  const versionedSource = getReviewedImageUrl(providerSizedSource)!
  return `/_next/image?url=${encodeURIComponent(versionedSource)}&w=${width}&q=${quality}`
}

export function getRetryImageUrl(src: string | null | undefined) {
  const normalizedSource = normalizeImageUrl(src)
  if (!normalizedSource || normalizedSource.startsWith("/")) return null

  try {
    const parsed = new URL(normalizedSource)
    if (parsed.hostname.toLowerCase() === "upload.wikimedia.org") {
      return null
    }
    parsed.searchParams.set("_hl_retry", "1")
    return parsed.toString()
  } catch {
    return null
  }
}

export function getCssImageUrl(src: string | null | undefined, options?: { width?: number; quality?: number }) {
  const imageUrl = getOptimizedImageUrl(src, options)
  return imageUrl ? `url(${JSON.stringify(imageUrl)})` : undefined
}

export function getPanelBackgroundImage(src: string | null | undefined, overlay: string) {
  const imageUrl = getCssImageUrl(src)
  return imageUrl ? `${overlay}, ${imageUrl}` : undefined
}
